import { Injectable, OnDestroy } from '@angular/core';
import { TimberMessageTypes } from '@shared/models/enums/enums';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { Subscription } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { LogParameters } from './interfaces/log.parameters';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { Timber } from '@itero/timber';
import { LoggingSettings } from '@shared/models/environment-params';
import { ILogParameters } from '@itero/timber';
import { IRemoteConfig } from '@itero/timber';
import { AppenderType, LogLevel } from '@itero/timber';
import { v4 as uuid } from 'uuid';
import { DoctorModel } from '@shared/models/rx-models/interfaces/doctor-model';
import { mask } from '@shared/utils/mask-util';
import { AuthInfoQuery } from '@shared/store/authInfo/auth-info-query';
import { Version as rxAppVersion } from '../../../../environments/app-version';

const DEFAULT_MAX_DATA_PART_LENGTH = 7000;

@Injectable({ providedIn: 'root' })
export class LoggerService implements OnDestroy {
	protected timber: Timber | null = null;
	protected biTimber: Timber;
	maxDataPartLength: number = DEFAULT_MAX_DATA_PART_LENGTH;
	private sessionId: string;
	private initialized = false;
	private readonly subscription = new Subscription();
	private appId: string;

	constructor(
		private readonly appSettingsService: AppSettingsService,
		private readonly shellQuery: ShellQuery,
		private readonly authInfoQuery: AuthInfoQuery,
		protected hostPlatformService: HostPlatformService
	) {
		this.sessionId = uuid();
		this.initIfReady();
	}

	static getTimberConfig(loggingSetting: LoggingSettings): IRemoteConfig {
		const { url, appId, requiredFields, level, appenderTypes, eventType } = loggingSetting;

		return {
			url: url ?? '',
			appId: appId ?? 'rx-app',
			requiredFields,
			minLogLevel: LogLevel[level as keyof typeof LogLevel] ?? LogLevel.Off,
			appenderTypes: appenderTypes
				.filter(x => Object.values(AppenderType).includes(x))
				.map(x => AppenderType[x as keyof typeof AppenderType]),
			eventType: eventType ?? undefined
		} satisfies IRemoteConfig;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.initialized = false;
		this.timber = null;
	}

	trace(message: string, parameters: LogParameters): void {
		this.logByType(message, TimberMessageTypes.trace, parameters);
	}

	debug(message: string, parameters: LogParameters): void {
		this.logByType(message, TimberMessageTypes.debug, parameters);
	}

	info(message: string, parameters: LogParameters): void {
		this.logByType(message, TimberMessageTypes.info, parameters);
	}

	warn(message: string, parameters: LogParameters): void {
		this.logByType(message, TimberMessageTypes.warn, parameters);
	}

	error(message: string, parameters: LogParameters): void {
		this.logByType(message, TimberMessageTypes.error, parameters);
	}

	fatal(message: string, parameters: LogParameters): void {
		this.logByType(message, TimberMessageTypes.fatal, parameters);
	}

	logRXData(rx: RxModel, text: string, module: string, extendedParameters?: Record<string, string>): any {
		const rxForLog = this.createRxForLog(rx);
		const loggingSetting = this.appSettingsService.loggingSetting;

		if (!loggingSetting) {
			return rxForLog;
		}

		const currLength = JSON.stringify(rx).length;
		const hasRemoteTimberLog = loggingSetting.appenderTypes.some(
			t => AppenderType[t as keyof typeof AppenderType] === AppenderType.Remote
		);

		if (hasRemoteTimberLog && currLength > this.maxDataPartLength) {
			this.logRxParts(JSON.stringify(rxForLog), text, 'rx', module, extendedParameters);
			this.logRxParts(JSON.stringify(rx.Bridges), text, 'Bridges', module, extendedParameters);
			this.logRxParts(JSON.stringify(rx.Teeth), text, 'Teeth', module, extendedParameters);
			this.logRxParts(mask(rx.Notes ?? ''), text, 'Notes', module, extendedParameters);
			this.logRxParts(mask(rx.AligntechNotes ?? ''), text, 'AligntechNotes', module, extendedParameters);

			return rxForLog;
		}
		this.info(`${text}: ${JSON.stringify(rxForLog)}`, {
			module,
			extendedParameters
		});

		return rxForLog;
	}

	private initIfReady(): void {
		if (this.initialized) {
			// init logger once app.settinsg are loaded
			// every service's method checks/initialize timber before execute its methods
			return;
		}

		const loggingSetting = this.appSettingsService.loggingSetting;

		if (!loggingSetting) {
			return;
		}

		this.initialized = true;
		this.maxDataPartLength = loggingSetting.maxDataPartLength ?? DEFAULT_MAX_DATA_PART_LENGTH;
		this.createLogger(loggingSetting);
		this.updateConfigWhenUserChanges();
		this.updateConfigWhenCompanyChanges();
	}

	private createLogger(loggingSettings: LoggingSettings): void {
		const fullConfig: IRemoteConfig = {
			...LoggerService.getTimberConfig(loggingSettings),
			userId: this.shellQuery.contactId ?? 0,
			companyId: this.shellQuery.companyId ?? 0
		};

		this.timber = new Timber(fullConfig);
		this.appId = loggingSettings?.appId ?? 'rx-app';
	}

	private logByType(message: string, messageType: TimberMessageTypes, parameters?: ILogParameters): void {
		const consoleMessage = `RxLog: ${message}, Parameters: ${JSON.stringify(parameters)}`;

		if (this.hostPlatformService.isScanner) {
			/* eslint-disable no-console */
			console.log(consoleMessage);
		}

		this.initIfReady();

		if (!this.timber) {
			// Timber not ready yet - fallback to console
			/* eslint-disable no-console */
			console[messageType === TimberMessageTypes.fatal ? 'error' : messageType](consoleMessage);

			return;
		}

		if (parameters) {
			parameters.extendedParameters = {
				...parameters.extendedParameters,
				platform: this.hostPlatformService.hostPlatform,
				sessionId: this.authInfoQuery.authInfo?.sessionId ?? this.sessionId,
				appVersion: rxAppVersion?.version
			};
		}
		this.timber[messageType](message, parameters);
	}

	private updateConfigWhenUserChanges(): void {
		this.subscription.add(
			this.shellQuery.contactId$
				.pipe(
					filter((contactId): contactId is number => !!contactId),
					take(1),
					tap(contactId => this.timber?.setConfig({ userId: contactId, appId: this.appId }))
				)
				.subscribe()
		);
	}

	private updateConfigWhenCompanyChanges(): void {
		this.subscription.add(
			this.shellQuery.companyId$
				.pipe(
					filter((companyId): companyId is number => !!companyId),
					take(1),
					tap(companyId => this.timber?.setConfig({ companyId, appId: this.appId }))
				)
				.subscribe()
		);
	}

	private createRxForLog(rx: RxModel) {
		const patient = { UID: rx.Patient?.UID };
		const doctor: {
			Id?: DoctorModel['Id'];
			Name?: DoctorModel['Name'];
		} = {
			Id: rx.Doctor?.Id
		};

		if (!doctor.Id) {
			doctor.Name = mask(rx.Doctor?.Name);
		}

		return {
			ID: rx.ID,
			CompanyID: rx.CompanyID,
			ContactID: rx.ContactID,
			Patient: patient,
			Order: rx.Order,
			Doctor: doctor,
			CompletionLevel: rx.CompletionLevel,
			ShadeSystemId: rx.ShadeSystemId,
			ClonedFromRxID: rx.ClonedFromRxID,
			TechnicalNotes: rx.TechnicalNotes,
			PrePrepScan: rx.PrePrepScan,
			MultiBiteScan: rx.MultiBiteScan,
			OrderInformation: rx.OrderInformation,
			TreatmentStage: rx.TreatmentStage,
			AlignerNumber: rx.AlignerNumber,
			IsAlta: rx.IsAlta,
			HasNIRI: rx.HasNIRI,
			CompanyName: rx.CompanyName,
			ForceRxFormUpdate: rx.ForceRxFormUpdate,
			NIRIEnabled: rx.NIRIEnabled,
			NIRIhasOnlyIOC: rx.NIRIhasOnlyIOC,
			ToothNumberingSystem: rx.ToothNumberingSystem,
			IsSleeveConfirmed: rx.IsSleeveConfirmed,
			IcastOrthoModel: rx.IcastOrthoModel,
			Version: rx.Version,
			DentureDetails: rx.DentureDetails,
			ApplianceDetails: rx.ApplianceDetails,
			EmergenceProfile: rx.EmergenceProfile,
			IsDraft: rx.IsDraft,
			MultiBiteRestoScan: rx.MultiBiteRestoScan,
			PalatalScanningFeedback: rx.PalatalScanningFeedback
		};
	}

	private logRxParts(
		data: string | null,
		message: string,
		partMessage: string,
		module: string,
		extendedParameters?: Record<string, string>
	) {
		const dataParts = this.splitData(data);

		if (dataParts == null) {
			return;
		}

		if (!extendedParameters?.traces && dataParts.length > 1) {
			extendedParameters = {
				logId: uuid()
			};
		}

		dataParts.forEach((part, i) => {
			if (!part) {
				return;
			}
			this.info(`${message} ${partMessage}${dataParts.length > 1 ? ` part ${i + 1}` : ''}: ${part}`, {
				module,
				extendedParameters
			});
		});
	}

	private splitData(data: string | null): string[] | null {
		if (!data || data === 'null') {
			return null;
		}

		if (data.length <= this.maxDataPartLength) {
			return [data];
		}

		const maxDataPart = data.substring(0, this.maxDataPartLength);
		const characterList = [',', '.', '}'];
		const indexList = characterList.map(character => maxDataPart.lastIndexOf(character)).filter(index => index > 0);

		const splitIndex = indexList.length > 0 ? Math.max(...indexList) : this.maxDataPartLength;

		const leftPart = data.substring(0, splitIndex);
		const rightPart = data.substring(splitIndex + 1);
		const rightParts = this.splitData(rightPart);

		return rightParts ? [leftPart, ...rightParts] : [leftPart];
	}
}
